// R10-3 — THE THREE ASTROCARTOGRAPHY REPORT VIEWS, AND THE SEVEN TYPES ONLY THEY NAME.
//
// Fifth, sixth and seventh shells out of App.tsx. Same entry requirement as every extraction before
// them and the same payoff: a component inside a 15,000-line TSX file cannot be imported by a .mjs
// gate, so `AcgMapView` — the biggest report view left, and the one that renders a member's whole
// personal geography — was held by nothing but a regex over its own source text.
//
// AcgPlanetGroups COMES WITH THEM, and it holds three hooks, which is normally the line an extraction
// does not cross (R8: moving a stateful component puts it BEYOND the instrument, which is the one
// outcome roadmap 7 must never produce). It costs no reach here: it was already beyond the loader
// inside App.tsx, and the stubbed JSX runtime never CALLS a child component — it turns
// `<AcgPlanetGroups .../>` into a plain `{ type, props }`. So a hookless caller that renders it is
// still fully callable, and `scripts/r10-report-views.mjs` reads the tree either way.
//
// Moved VERBATIM. `ACG_THEME_LABELS` was already out in src/catalogs/glyphs.ts (R6-2), and the
// narrative shell these three all render through was already out (R9-1).

import { useEffect, useMemo, useState } from "react";
import { NarrativeReportView, type NarrativeReportData } from "./NarrativeReportView";
import { ACG_THEME_LABELS } from "../catalogs/glyphs";
import { ordinal } from "../ordinals.js";
import { displayPoint } from "../astroNames.js";

export type AcgPlaceHit = { name: string; country: string; countryCode?: string; distanceKm: number };
export type AcgPersonalLine = {
  planet: string; angle: string; lineType: string; meaning?: string; strength?: string;
  keywords?: string[]; cities: AcgPlaceHit[]; countries: string[]; totalNear: number;
};
export type AcgBestPlace = {
  name: string; country: string; rating: number; score: number;
  support: { planet: string; angle: string; distanceKm: number }[];
  strain?: { planet: string; angle: string; distanceKm: number }[];
};
// AC6: the detail is grouped by planet, strongest first, each line carrying a beginner-language
// subtitle for its angle and its own tightened narrative.
export type AcgGroupedLine = AcgPersonalLine & { angleLabel?: string; narrative?: string; rank?: number };
export type AcgPlanetGroup = {
  planet: string; strength?: string; rank?: number; summary?: string;
  countries?: string[]; lines: AcgGroupedLine[];
};
export type AcgMapData = NarrativeReportData & {
  svg?: string;
  lines?: { planet: string; lineType: string }[];
  personalLines?: AcgPersonalLine[];
  bestPlaces?: AcgBestPlace[];
  themes?: Record<string, { name: string; country: string; planet: string; angle: string }[]>;
  placesOrbKm?: number;
  primer?: string;
  planetGroups?: AcgPlanetGroup[];
  // HOUSE READINGS (e): written only under the server's READINGS_V2 switch. Absent on every live map.
  placeGuide?: AcgPlaceGuide;
};
type AcgGuidePlace = { name: string; state?: string; country: string; lines: string[] };
export type AcgPlaceGuide = {
  regions: { region: string; favours: AcgGuidePlace[]; harder: AcgGuidePlace[]; body: string }[];
  quietRegions?: string[];
  world: { favours: AcgGuidePlace[]; harder: AcgGuidePlace[]; body: string; harderBody: string };
};
const guideName = (p: AcgGuidePlace) => (p.state ? `${p.name}, ${p.state}` : `${p.name}, ${p.country}`);

// HOUSE READINGS (e) — THE MAP AS A GUIDE TO PLACES (her law): the United States by region, then
// the world, each saying where her lines favour her and where they run harder, by name.
function AcgPlaceGuideView({ guide }: { guide: AcgPlaceGuide }) {
  return (
    <section className="acg-best acg-guide" aria-label="Where your lines favour you">
      <h3>Where your lines favour you</h3>
      {guide.regions.map((r) => (
        <div key={r.region} className="acg-best__theme acg-guide__region">
          <h4>{r.region}</h4>
          <p>{r.body}</p>
          {r.favours.length ? <p className="acg-best__note">Favours you: {r.favours.map(guideName).join(" · ")}</p> : null}
          {r.harder.length ? <p className="acg-best__note">Runs harder: {r.harder.map(guideName).join(" · ")}</p> : null}
        </div>
      ))}
      {guide.quietRegions?.length ? (
        <p className="acg-best__note">None of your lines reach the {guide.quietRegions.join(", ")}: quiet ground for you.</p>
      ) : null}
      <div className="acg-best__theme acg-guide__region">
        <h4>Around the world</h4>
        <p>{guide.world.body}</p>
        {guide.world.favours.length ? <p className="acg-best__note">Favours you: {guide.world.favours.map(guideName).join(" · ")}</p> : null}
      </div>
      <div className="acg-best__theme acg-guide__region">
        <h4>Where it runs harder</h4>
        <p>{guide.world.harderBody}</p>
        {guide.world.harder.length ? <p className="acg-best__note">{guide.world.harder.map(guideName).join(" · ")}</p> : null}
      </div>
    </section>
  );
}

// R6-2: ACG_THEME_LABELS moved with the other symbol tables to `src/catalogs/glyphs.ts`.
export type AcgLocationData = NarrativeReportData & {
  place?: string;
  score?: number | null;
  nearbyLines?: { planet: string; lineType: string; distanceKm: number; strength: string; meaning: string }[];
};
export type AcgRelocationData = NarrativeReportData & {
  place?: string;
  shifts?: { ascendant: number; midheaven: number };
  houseChanges?: { planet: string; from: number; to: number }[];
};

// The three astrocartography views all reuse the ONE designed narrative report and add
// their own evidence through its `extras` slot: the map SVG (server-sanitized before it
// ever reaches the DOM), the real lines reaching a place, the angle shifts on a move.
// AC6-2: the planet groups. Collapsed by default so the reading is a short page a beginner can
// take in, EXCEPT the member's strongest planets, which start open because that is what they came
// for. Reuses the room's existing accordion pattern rather than inventing a second one.
export function AcgPlanetGroups({ groups, orbKm }: { groups: AcgPlanetGroup[]; orbKm?: number }) {
  // The 2-3 strongest start open. Ties do not force a fourth open: the cap is what keeps the page
  // short enough to read.
  const openByDefault = useMemo(() => new Set(groups.slice(0, 3).map((g) => g.planet)), [groups]);
  const [open, setOpen] = useState<Set<string>>(openByDefault);
  useEffect(() => { setOpen(openByDefault); }, [openByDefault]);
  const toggle = (planet: string) => setOpen((cur) => {
    const next = new Set(cur);
    if (next.has(planet)) next.delete(planet); else next.add(planet);
    return next;
  });

  return (
    <section className="acg-groups" aria-label="Your lines, planet by planet">
      <h3>Your lines, planet by planet</h3>
      <p className="acg-best__note">
        Strongest first. Open any planet to see its four lines and the real ground each one crosses
        {typeof orbKm === "number" ? ` (within ${orbKm} km)` : ""}.
      </p>
      <div className="acg-groups__list">
        {groups.map((g) => {
          const isOpen = open.has(g.planet);
          return (
            <div key={g.planet} className={`acg-group${isOpen ? " is-open" : ""}`}>
              <button
                type="button"
                className="acg-group__toggle"
                aria-expanded={isOpen}
                onClick={() => toggle(g.planet)}
              >
                <span className="acg-group__name">{displayPoint(g.planet)}</span>
                {g.strength ? (
                  <span className="acg-group__chip">{String(g.strength).replace(/_/g, " ")}</span>
                ) : null}
                <span className="acg-group__summary">{g.summary}</span>
                <span className="acg-group__caret" aria-hidden="true">{isOpen ? "−" : "+"}</span>
              </button>
              {isOpen ? (
                <div className="acg-group__body">
                  {g.lines.map((l) => (
                    <div key={`${l.planet}-${l.lineType}`} className="acg-angle">
                      <p className="acg-angle__head">
                        <strong>{l.angle}</strong>
                        {/* AC6-3: the beginner subtitle is what stops the four angles reading alike. */}
                        {l.angleLabel ? <span className="acg-angle__label">{l.angleLabel}</span> : null}
                        {l.strength ? <span className="acg-angle__strength">{String(l.strength).replace(/_/g, " ")}</span> : null}
                      </p>
                      {l.narrative ? <p className="acg-angle__read">{l.narrative}</p> : null}
                      <p className="acg-angle__places">
                        {l.cities.map((c) => `${c.name}, ${c.country} (${c.distanceKm} km)`).join(" · ")}
                      </p>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          );
        })}
      </div>
    </section>
  );
}

export function AcgMapView({ data }: { data: AcgMapData }) {
  return (
    <NarrativeReportView
      data={data}
      extras={
        <>
          {data.svg ? (
            <section className="acg-map" aria-label="Your astrocartography map">
              <div className="acg-map__svg" dangerouslySetInnerHTML={{ __html: data.svg }} />
            </section>
          ) : null}
          {data.placeGuide ? <AcgPlaceGuideView guide={data.placeGuide} /> : null}
          {/* AC5-2: where they rank highest, stated outright, so nobody guesses cities one at a
              time. Ranked by which of their own benefic lines reach each place and how closely. */}
          {data.bestPlaces?.length ? (
            <section className="acg-best" aria-label="Your highest rated places">
              <h3>Where the earth works hardest for you</h3>
              <p className="acg-best__note">
                Ranked from your own lines. 100 is the strongest ground on your map, and everything else is
                measured against it.
              </p>
              <ol className="acg-best__list">
                {data.bestPlaces.map((p) => (
                  <li key={`${p.name}-${p.country}`} className="acg-best__item">
                    <span className="acg-best__rank" aria-hidden="true">{p.rating}</span>
                    <span className="acg-best__place">
                      {/* Name and country are ONE grid child on purpose: as separate children the
                          grid broke them onto two lines and left a comma leading the second. */}
                      <span className="acg-best__title"><strong>{p.name}</strong>, {p.country}</span>
                      <span className="acg-best__why">
                        {p.support.map((s) => `${displayPoint(s.planet)} ${s.angle}`).join(", ")}
                        {p.support[0] ? ` within ${p.support[0].distanceKm} km` : ""}
                      </span>
                    </span>
                  </li>
                ))}
              </ol>
              {data.themes && Object.keys(data.themes).length ? (
                <div className="acg-best__themes">
                  {Object.entries(data.themes).map(([theme, places]) => (
                    <div key={theme} className="acg-best__theme">
                      <h4>{ACG_THEME_LABELS[theme] || theme}</h4>
                      <p>{places.map((p) => `${p.name}, ${p.country}`).join(" · ")}</p>
                    </div>
                  ))}
                </div>
              ) : null}
            </section>
          ) : null}

          {/* AC6-1: the primer sits between the payoff and the detail, so a beginner is oriented
              before they meet a single line. */}
          {data.primer ? (
            <section className="acg-primer" aria-label="How to read your map">
              <h3>How to read your map</h3>
              <p>{data.primer}</p>
            </section>
          ) : null}

          {/* AC6-2/3/4: the detail, grouped by planet, strongest first, collapsed by default. */}
          {data.planetGroups?.length ? (
            <AcgPlanetGroups groups={data.planetGroups} orbKm={data.placesOrbKm} />
          ) : data.personalLines?.length ? (
            // Older saved readings predate the grouping and still render their flat list.
            <section className="acg-personal" aria-label="Where your lines run">
              <h3>Where each of your lines runs</h3>
              <p className="acg-best__note">
                Every line your chart draws, and the real places it passes through or close to
                {typeof data.placesOrbKm === "number" ? ` (within ${data.placesOrbKm} km)` : ""}.
              </p>
              <ul className="acg-personal__list">
                {data.personalLines.map((l) => (
                  <li key={`${l.planet}-${l.lineType}`} className="acg-personal__line">
                    <p className="acg-personal__head">
                      <strong>{displayPoint(l.planet)} {l.angle}</strong>
                      {l.strength ? <span className="acg-personal__strength">{String(l.strength).replace(/_/g, " ")}</span> : null}
                    </p>
                    <p className="acg-personal__places">
                      {l.cities.map((c) => `${c.name}, ${c.country} (${c.distanceKm} km)`).join(" · ")}
                    </p>
                    {l.meaning ? <p className="acg-personal__meaning">{l.meaning}</p> : null}
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {data.lines?.length ? (
            <section className="acg-evidence">
              <h3>Your lines</h3>
              <p className="acg-evidence__lines">{data.lines.map((l) => `${displayPoint(l.planet)} ${l.lineType}`).join(" · ")}</p>
            </section>
          ) : null}
        </>
      }
    />
  );
}

export function AcgLocationView({ data }: { data: AcgLocationData }) {
  return (
    <NarrativeReportView
      data={data}
      extras={
        <section className="acg-evidence">
          {typeof data.score === "number" ? (
            <p className="acg-evidence__score">Overall support for {data.place}: <strong>{data.score}</strong> out of 100</p>
          ) : null}
          {data.nearbyLines?.length ? (
            <>
              <h3>The lines that reach {data.place}</h3>
              <ul>
                {data.nearbyLines.map((l) => (
                  <li key={`${l.planet}-${l.lineType}`}>
                    <strong>{displayPoint(l.planet)} {l.lineType}</strong>, {l.distanceKm} km away ({l.strength}). {l.meaning}
                  </li>
                ))}
              </ul>
            </>
          ) : <p>No planetary lines run close to {data.place}. That is its own kind of answer.</p>}
        </section>
      }
    />
  );
}

export function AcgRelocationView({ data }: { data: AcgRelocationData }) {
  return (
    <NarrativeReportView
      data={data}
      extras={
        <section className="acg-evidence">
          {data.shifts ? (
            <div className="acg-evidence__shifts">
              <div><span>Ascendant shift</span><strong>{data.shifts.ascendant.toFixed(1)}°</strong></div>
              <div><span>Midheaven shift</span><strong>{data.shifts.midheaven.toFixed(1)}°</strong></div>
            </div>
          ) : null}
          {data.houseChanges?.length ? (
            <>
              <h3>What moves at {data.place}</h3>
              <ul>
                {/* W15 — HER TWO CAPTURES CAME OFF THIS ONE LINE. It glued `th` onto a raw number
                    ("your 3th house", "your 1th here" — and `from: 12, to: 1` is in real saved
                    payloads) and printed the vendor's own token for the point (`Mean_Node`). Both
                    now read the one law: `ordinal` from src/ordinals.js and `displayPoint` from
                    src/astroNames.js, neither of which this file is allowed to reimplement. */}
                {data.houseChanges.map((c) => (
                  <li key={c.planet}><strong>{displayPoint(c.planet)}</strong>: your {ordinal(c.from)} house at home, your {ordinal(c.to)} here</li>
                ))}
              </ul>
            </>
          ) : null}
        </section>
      }
    />
  );
}
